import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import api from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";

export default function SettingsManager({ onChange }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setData(data);
    } catch {
      toast.error("Could not load settings");
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) {
    return <div className="py-20 text-center text-[#94A3B8]">Loading...</div>;
  }

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data: updated } = await api.put("/admin/settings", patch);
      setData(updated);
      onChange && onChange();
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-manager" className="space-y-8">
      {/* Brand */}
      <BrandSection brand={data.brand} onSave={(brand) => save({ brand })} saving={saving} />
      {/* Hero */}
      <HeroSection hero={data.hero} onSave={(hero) => save({ hero })} saving={saving} />
      {/* Stats */}
      <StatsSection stats={data.stats || []} onSave={(stats) => save({ stats })} saving={saving} />
      {/* About */}
      <AboutSection about={data.about || {}} onSave={(about) => save({ about })} saving={saving} />
      {/* Industries */}
      <IndustriesSection industries={data.industries || []} onSave={(industries) => save({ industries })} saving={saving} />
      {/* Footer note */}
      <FooterNoteSection note={data.footer_note || ""} onSave={(footer_note) => save({ footer_note })} saving={saving} />
    </div>
  );
}

/* ---------- helpers ---------- */
function Card({ title, children, code }) {
  return (
    <div className="border border-[#1F3550] bg-[#0D1B2A] p-6 lg:p-8">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F39C12]">{code}</div>
      <h3 className="mt-1 font-heading font-bold text-xl text-[#F8FAFC] mb-6">{title}</h3>
      {children}
    </div>
  );
}
function Label({ children }) {
  return <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{children}</label>;
}
function Input(props) {
  return (
    <input
      {...props}
      className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none placeholder:text-[#64748B]"
    />
  );
}
function TextArea(props) {
  return (
    <textarea
      {...props}
      className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none placeholder:text-[#64748B]"
    />
  );
}
function SaveBtn({ saving, testid = "settings-save" }) {
  return (
    <button
      type="submit"
      disabled={saving}
      data-testid={testid}
      className="mt-6 inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-6 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] transition-colors disabled:opacity-50"
    >
      <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
    </button>
  );
}

/* ---------- Brand ---------- */
function BrandSection({ brand, onSave, saving }) {
  const [f, setF] = useState(brand);
  useEffect(() => setF(brand), [brand]);
  const ch = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Card code="SET/01" title="Company Info">
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="brand-form">
        <div><Label>Company Name</Label><Input data-testid="brand-name" value={f.name || ""} onChange={ch("name")} /></div>
        <div><Label>Tagline</Label><Input data-testid="brand-tagline" value={f.tagline || ""} onChange={ch("tagline")} /></div>
        <div><Label>Established Year</Label><Input data-testid="brand-established" value={f.established || ""} onChange={ch("established")} /></div>
        <div><Label>City</Label><Input data-testid="brand-city" value={f.city || ""} onChange={ch("city")} /></div>
        <div><Label>Email</Label><Input data-testid="brand-email" type="email" value={f.email || ""} onChange={ch("email")} /></div>
        <div><Label>Phone</Label><Input data-testid="brand-phone" value={f.phone || ""} onChange={ch("phone")} /></div>
        <div className="md:col-span-2"><Label>Address</Label><Input data-testid="brand-address" value={f.address || ""} onChange={ch("address")} /></div>
        <div className="md:col-span-2"><Label>Working Hours</Label><Input data-testid="brand-hours" value={f.working_hours || ""} onChange={ch("working_hours")} /></div>
        <div className="md:col-span-2">
          <ImageUpload label="Logo" value={f.logoUrl} onChange={(url) => setF({ ...f, logoUrl: url })} testid="brand-logo" />
        </div>
        <div className="md:col-span-2"><SaveBtn saving={saving} testid="brand-save" /></div>
      </form>
    </Card>
  );
}

/* ---------- Hero ---------- */
function HeroSection({ hero, onSave, saving }) {
  const [f, setF] = useState(hero);
  useEffect(() => setF(hero), [hero]);
  const ch = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Card code="SET/02" title="Home Hero">
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="hero-form">
        <div className="md:col-span-2"><Label>Eyebrow (small text above headline)</Label><Input data-testid="hero-eyebrow" value={f.eyebrow || ""} onChange={ch("eyebrow")} /></div>
        <div><Label>Headline (line 1)</Label><Input data-testid="hero-headline-top" value={f.headline_top || ""} onChange={ch("headline_top")} /></div>
        <div><Label>Accent word (orange)</Label><Input data-testid="hero-headline-accent" value={f.headline_accent || ""} onChange={ch("headline_accent")} /></div>
        <div className="md:col-span-2"><Label>Headline tail</Label><Input data-testid="hero-headline-tail" value={f.headline_tail || ""} onChange={ch("headline_tail")} /></div>
        <div className="md:col-span-2"><Label>Subtitle paragraph</Label><TextArea rows={3} data-testid="hero-subtitle" value={f.subtitle || ""} onChange={ch("subtitle")} /></div>
        <div className="md:col-span-2"><ImageUpload label="Hero background image" value={f.image} onChange={(url) => setF({ ...f, image: url })} testid="hero-image" /></div>
        <div className="md:col-span-2"><SaveBtn saving={saving} testid="hero-save" /></div>
      </form>
    </Card>
  );
}

/* ---------- Stats ---------- */
function StatsSection({ stats, onSave, saving }) {
  const [list, setList] = useState(stats);
  useEffect(() => setList(stats), [stats]);
  const update = (i, k, v) => setList(list.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const add = () => setList([...list, { value: "", label: "" }]);
  const remove = (i) => setList(list.filter((_, idx) => idx !== i));
  return (
    <Card code="SET/03" title="Stats (Home + About)">
      <div className="space-y-3" data-testid="stats-form">
        {list.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-4"><Label>Value</Label><Input data-testid={`stat-value-${i}`} value={s.value} onChange={(e) => update(i, "value", e.target.value)} placeholder="20+" /></div>
            <div className="col-span-7"><Label>Label</Label><Input data-testid={`stat-label-${i}`} value={s.label} onChange={(e) => update(i, "label", e.target.value)} placeholder="Years of Experience" /></div>
            <button onClick={() => remove(i)} className="col-span-1 border border-[#1F3550] text-[#94A3B8] hover:text-[#EF4444] hover:border-[#EF4444] py-2.5 transition-colors" data-testid={`stat-remove-${i}`}>
              <Trash2 className="h-4 w-4 mx-auto" />
            </button>
          </div>
        ))}
        <button type="button" onClick={add} data-testid="stat-add" className="mt-2 inline-flex items-center gap-2 border border-[#F39C12] text-[#F39C12] px-4 py-2 font-bold uppercase tracking-widest text-[10px] hover:bg-[#F39C12]/10 transition-colors">
          <Plus className="h-4 w-4" /> Add Stat
        </button>
        <div><button onClick={() => onSave(list)} disabled={saving} data-testid="stats-save" className="mt-4 inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-6 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] disabled:opacity-50"><Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Stats"}</button></div>
      </div>
    </Card>
  );
}

/* ---------- About ---------- */
function AboutSection({ about, onSave, saving }) {
  const [f, setF] = useState(about);
  useEffect(() => setF(about), [about]);
  const ch = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setPara = (i, v) => setF({ ...f, story_paragraphs: (f.story_paragraphs || []).map((p, idx) => idx === i ? v : p) });
  const addPara = () => setF({ ...f, story_paragraphs: [...(f.story_paragraphs || []), ""] });
  const rmPara = (i) => setF({ ...f, story_paragraphs: (f.story_paragraphs || []).filter((_, idx) => idx !== i) });
  const values = f.values || [];
  const setVal = (i, k, v) => setF({ ...f, values: values.map((x, idx) => idx === i ? { ...x, [k]: v } : x) });
  const addVal = () => setF({ ...f, values: [...values, { title: "", description: "" }] });
  const rmVal = (i) => setF({ ...f, values: values.filter((_, idx) => idx !== i) });
  return (
    <Card code="SET/04" title="About Page Content">
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-5" data-testid="about-form">
        <div><Label>Headline</Label><Input data-testid="about-headline" value={f.headline || ""} onChange={ch("headline")} /></div>
        <div><Label>Intro paragraph</Label><TextArea rows={3} data-testid="about-intro" value={f.intro || ""} onChange={ch("intro")} /></div>
        <div>
          <Label>Story paragraphs</Label>
          <div className="space-y-2 mt-2">
            {(f.story_paragraphs || []).map((p, i) => (
              <div key={i} className="flex gap-2">
                <TextArea rows={3} value={p} onChange={(e) => setPara(i, e.target.value)} data-testid={`about-para-${i}`} />
                <button type="button" onClick={() => rmPara(i)} className="border border-[#1F3550] text-[#94A3B8] hover:text-[#EF4444] hover:border-[#EF4444] px-3"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button type="button" onClick={addPara} data-testid="about-para-add" className="inline-flex items-center gap-2 border border-[#F39C12] text-[#F39C12] px-4 py-2 font-bold uppercase tracking-widest text-[10px] hover:bg-[#F39C12]/10"><Plus className="h-4 w-4" /> Add Paragraph</button>
          </div>
        </div>
        <ImageUpload label="Story image" value={f.story_image} onChange={(url) => setF({ ...f, story_image: url })} testid="about-image" />
        <div><Label>Mission</Label><TextArea rows={3} data-testid="about-mission" value={f.mission || ""} onChange={ch("mission")} /></div>
        <div><Label>Vision</Label><TextArea rows={3} data-testid="about-vision" value={f.vision || ""} onChange={ch("vision")} /></div>
        <div>
          <Label>Core Values</Label>
          <div className="space-y-2 mt-2">
            {values.map((v, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start border border-[#1F3550] p-3">
                <div className="col-span-4"><Input value={v.title} onChange={(e) => setVal(i, "title", e.target.value)} placeholder="Value title" data-testid={`about-val-title-${i}`} /></div>
                <div className="col-span-7"><TextArea rows={2} value={v.description} onChange={(e) => setVal(i, "description", e.target.value)} placeholder="Value description" data-testid={`about-val-desc-${i}`} /></div>
                <button type="button" onClick={() => rmVal(i)} className="col-span-1 border border-[#1F3550] text-[#94A3B8] hover:text-[#EF4444] hover:border-[#EF4444] py-2.5 mt-2"><Trash2 className="h-4 w-4 mx-auto" /></button>
              </div>
            ))}
            <button type="button" onClick={addVal} data-testid="about-val-add" className="inline-flex items-center gap-2 border border-[#F39C12] text-[#F39C12] px-4 py-2 font-bold uppercase tracking-widest text-[10px] hover:bg-[#F39C12]/10"><Plus className="h-4 w-4" /> Add Value</button>
          </div>
        </div>
        <SaveBtn saving={saving} testid="about-save" />
      </form>
    </Card>
  );
}

/* ---------- Industries ---------- */
function IndustriesSection({ industries, onSave, saving }) {
  const [text, setText] = useState((industries || []).join("\n"));
  useEffect(() => setText((industries || []).join("\n")), [industries]);
  return (
    <Card code="SET/05" title="Industries Served">
      <Label>One industry per line</Label>
      <TextArea rows={6} value={text} onChange={(e) => setText(e.target.value)} data-testid="industries-text" />
      <button onClick={() => onSave(text.split("\n").map((x) => x.trim()).filter(Boolean))} disabled={saving} data-testid="industries-save" className="mt-4 inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-6 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] disabled:opacity-50">
        <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
      </button>
    </Card>
  );
}

/* ---------- Footer note ---------- */
function FooterNoteSection({ note, onSave, saving }) {
  const [v, setV] = useState(note);
  useEffect(() => setV(note), [note]);
  return (
    <Card code="SET/06" title="Footer Compliance Note">
      <Label>Right side of the footer (e.g., certifications)</Label>
      <Input value={v} onChange={(e) => setV(e.target.value)} data-testid="footer-note" />
      <button onClick={() => onSave(v)} disabled={saving} data-testid="footer-note-save" className="mt-4 inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-6 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] disabled:opacity-50">
        <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
      </button>
    </Card>
  );
}
